import { App, Upload } from 'antd';
import { debounce } from 'lodash';
import React, { FC, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateProps } from '../../../contextRedux/Company/CompanyModel';
import { AnimateButton } from '../../../customComponents/button/CustomButton';
import { SearchInput } from '../../../customComponents/input/SearchInput';
import { CustomTable } from '../../../customComponents/table/CustomTable';
import CompanyService from '../../../services/company/CompanyService';
import { RootState } from '../../../store';
import { saveBlobCrossPlatform } from '../../../utils/saveBlobCrossPlatform';
import { CompanyColumn } from './CompanyColumn';

const CompanyTable: FC<any> = () => {
  const dispatch = useDispatch();
  const { message } = App.useApp();

  const reduxCompany = useSelector((state: RootState) => state.models?.company);
  const companies = useSelector((state: RootState) => state.store?.company.data ?? []);
  const companyTiers = useSelector((state: RootState) => state.store?.companyTier.data ?? []);

  const pageSizes = [10, 20, 30, 50];
  const currentPage = Math.floor(reduxCompany.props.skip / reduxCompany.props.take) + 1;

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      dispatch(updateProps({ field: 'searchTerm', value }));
      dispatch(updateProps({ field: 'skip', value: 0 }));
    }, 500),
    [dispatch]
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value);
  };

  const handleEdit = (record: any) => {
    window.open(`/panel/company/edit?id=${record?.id}`, '_blank');
  };

  const printToPdf = (record: any) => {
    CompanyService.print({
      ids: [record.id],
    })
      .then((response) => {
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'file.pdf';

        if (contentDisposition) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, '');
          }
        }

        return response.blob().then((blob: any) => {
          saveBlobCrossPlatform(blob, filename);
        });
      })
      .catch((error) => {
        console.error('Error processing your request:', error);
      });
  };

  const downloadGDPR = (record: any) => {
    CompanyService.downloadFile(record.id)
      .then((response) => {
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'file.pdf';

        if (contentDisposition) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, '');
          }
        }

        return response.blob().then((blob: any) => {
          saveBlobCrossPlatform(blob, filename);
        });
      })
      .catch((error) => {
        console.error('Error processing your request:', error);
      });
  };

  const getUploadProps = (id: any) => ({
    listType: 'text' as 'text',
    maxCount: 1,
    showUploadList: false,
    beforeUpload: (file: any) => beforeUpload(id, file),
    onChange: (info: any) => {
      if (info.file.status === 'done') {
        handleUpdate(id, info.file);
      } else if (info.file.status === 'error') {
        message.error(`${info.file.name} imaginea nu a fost incarcata.`);
      }
    },
  });

  const handleUpdate = async (id: any, file: any) => {
    const formData = new FormData();
    formData.append('id', id);
    formData.append('file', file);

    try {
      const response = await CompanyService.upload(formData);

      const data = await response.json();
      if (data?.errors?.length > 0) {
        message.error(data?.errors[0]);
      } else {
        message.success('PDF incarcat!');
        //search();
      }
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const beforeUpload = (id: any, file: any) => {
    const isPdf = file.type === 'application/pdf';
    if (!isPdf) {
      message.error('Poti adauga doar PDF!');
      return Upload.LIST_IGNORE;
    }
    handleUpdate(id, file);
    return false;
  };

  const paginationConfig = {
    current: currentPage,
    pageSize: reduxCompany.props.take,
    total: reduxCompany.props.totalItems,
    onChange: (page: number, pageSize: number) => {
      const skipValue = (page - 1) * pageSize;
      dispatch(updateProps({ field: 'skip', value: skipValue }));
      dispatch(updateProps({ field: 'take', value: pageSize }));
    },
    showSizeChanger: true,
    pageSizeOptions: pageSizes.map((size) => size.toString()),
    onShowSizeChange: (_current: number, size: number) => {
      dispatch(updateProps({ field: 'skip', value: 0 }));
      dispatch(updateProps({ field: 'take', value: size }));
    },
    showQuickJumper: false,
  };

  return (
    <>
      <div className='search-options'>
        <AnimateButton onClick={() => window.open(`/panel/company/new`, '_blank')}>Adauga</AnimateButton>
        <SearchInput
          placeholder='Search...'
          defaultValue={reduxCompany.props.searchTerm}
          onChange={handleSearch}
          style={{ marginBottom: 10 }}
        />
      </div>
      <CustomTable
        initialDataSource={companies ?? []}
        pagination={paginationConfig}
        columns={CompanyColumn(handleEdit, printToPdf, downloadGDPR, getUploadProps, companyTiers)}
        rowKey='id'
      />
    </>
  );
};

export default CompanyTable;
